import type { Route } from '@metro/routing'

/**
 * localStorage key under which the user's last-selected Routing
 * variant index is persisted. Distinct from {@link
 * import('./routeSelectionPersistence').KEY} (`metro:routeSelection`,
 * which carries From/To anchors): the variant index belongs to a
 * different lifecycle slice (`routeStatus`) and a different concern
 * (which of the N computed routes was last viewed). Keeping the keys
 * separate avoids cross-talk on partial writes and lets either slice
 * be cleared without disturbing the other.
 */
export const KEY = 'metro:routing.selectedVariant' as const

/**
 * Shape of the persisted Routing variant selection.
 *
 * - `variantIndex` — 0-based index into the variants array the user
 *   was last viewing. Clamped on restore (so a stale persisted index
 *   from a session with more variants does not throw the UI off).
 * - `variantsSignature` — a stable identity for the variants set that
 *   produced `variantIndex`. Built by {@link buildVariantsSignature}
 *   from `route.id` values in order. Used to detect a divergent
 *   variants list (different graph, different From/To, different
 *   algorithm cut) and fall back to index 0 when it changes — see UI
 *   design spec §4.
 */
export interface PersistedVariant {
  readonly variantIndex: number
  readonly variantsSignature: string
}

function isPositiveFiniteInteger(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0
  )
}

function isPersistedShape(value: unknown): value is PersistedVariant {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return isPositiveFiniteInteger(v.variantIndex) && typeof v.variantsSignature === 'string'
}

/**
 * Read the persisted variant selection from localStorage.
 *
 * Returns `null` if:
 * - nothing has been stored yet,
 * - the stored value is not valid JSON,
 * - the payload does not match {@link PersistedVariant} (missing or
 *   non-numeric `variantIndex`, missing/non-string `variantsSignature`,
 *   negative or fractional index),
 * - localStorage access throws (private mode, disabled storage, etc.).
 *
 * Never throws — callers can treat a `null` return as "no prior selection".
 */
export function load(): PersistedVariant | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw === null) return null
    const parsed = JSON.parse(raw) as unknown
    return isPersistedShape(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Persist a variant selection to localStorage, overwriting any
 * previous value. Silently swallows errors from `localStorage.setItem`
 * (quota exceeded, private-mode browsers, disabled storage). Persistence
 * is best-effort: the app must remain functional without it.
 */
export function save(value: PersistedVariant): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(value))
  } catch {
    // localStorage may be unavailable (private mode, quota). Drop silently.
  }
}

/**
 * Build a stable signature for a variants set — concatenated `route.id`
 * values joined with `'|'`. Empty array → empty string. Used as the
 * cross-session identity check for "are these the same variants the
 * user saw last time?" — different routes, different signatures.
 */
export function buildVariantsSignature(routes: readonly Route[]): string {
  return routes.map((r) => r.id).join('|')
}
